import { Component, effect, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { BackendService } from '@app/@shared/backend.service';
import { SecureFileService } from '@app/@shared/secure-file.service';
import { ProjectService } from '@app/shell/header/header.project.service';
import { Chart, ChartConfiguration, LinearScale, CategoryScale } from 'chart.js';
import { BoxPlotController, BoxAndWiskers, ViolinController, Violin } from '@sgratzl/chartjs-chart-boxplot';
import { faTableCells } from '@fortawesome/free-solid-svg-icons';
import { FLAIRR_NAV_CONFIG } from '@shared/analysis-nav/nav-configs';
import { NavConfig } from '@shared/analysis-nav/analysis-nav.component';
import { TableExportService } from '@shared/table-export.service';
import type { ExportColumn, ExportRow } from '@shared/table-export.service';
import {
  normalizeNumericTableRows,
  isNumericTableColumn,
  SMALL_TABLE_FILTER_BUTTON_PROPS,
} from '@shared/table-filter.utils';
import { FilterMatchMode } from '@openng/optimus-ui/api';
import type { FilterMetadata } from '@openng/optimus-ui/api';
import type { Table, TableFilterEvent } from '@openng/optimus-ui/table';

type TableFilters = Record<string, FilterMetadata | FilterMetadata[]>;

interface SummaryData {
  desc: string;
  cols: ExportColumn[];
  data: SummaryRow[];
}

interface SummaryLink {
  dataLink: string;
  sample: string | null;
  label: string;
}

interface SummaryRow extends ExportRow {
  sample: string;
  locus: string;
  shannon: string;
  simpson: string;
  cloneMapLinks: SummaryLink[];
}

@Component({
  // This component is activated through routing and is not used as an element selector.
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'flairr-summary',
  templateUrl: './flairr-summary.component.html',
  styleUrl: './flairr-summary.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class FlairrSummaryComponent implements OnInit {
  isLoading = false;
  public projectViewData: SummaryData = { desc: '', cols: [], data: [] };
  public selectedProjectID: string = '';

  // Navigation configuration - flairr-summary is for FLAIRR analysis only
  get navConfig(): NavConfig {
    return FLAIRR_NAV_CONFIG;
  }

  get analysisType(): 'capture' | 'flairr' {
    return 'flairr';
  }

  searchActive: boolean = false;
  compareSelectedRows: boolean = false;
  shannonPlotData: any = null;
  simpsonPlotData: any = null;
  summaryFilters: TableFilters = this.createTableFilters();
  globalFilter = '';
  filteredSummaryRows: SummaryRow[] = [];
  exportMessage = '';
  readonly pageSizes = [10, 20, 50, 200, 500];
  readonly faTableCells = faTableCells;
  readonly filterButtonProps = SMALL_TABLE_FILTER_BUTTON_PROPS;

  get globalFilterFields(): string[] {
    return this.projectViewData.cols.map((column) => column.data);
  }

  chartOptions: ChartConfiguration<'violin'>['options'] = {
    responsive: true,
    indexAxis: 'y',
    maintainAspectRatio: true,
    elements: {},
    scales: {
      x: {
        display: true,
        title: { text: '', display: false },
      },
      y: {
        display: true,
      },
    },
  };

  constructor(
    private backendService: BackendService,
    public projectService: ProjectService,
    private secureFileService: SecureFileService,
    private exportService: TableExportService,
  ) {
    effect(() => {
      this.setProject();
    }, {});
  }

  ngOnInit(): void {
    Chart.register(BoxPlotController, BoxAndWiskers, ViolinController, Violin, LinearScale, CategoryScale);
  }

  // Haplotype / novel allele charts

  // Project / locus selection

  setProject() {
    if (this.projectService.selectedProjectDetails() !== null) {
      const projectDetails = this.projectService.selectedProjectDetails();
      this.selectedProjectID = projectDetails ? projectDetails.project_id : '';

      console.log('setting project: ', this.selectedProjectID);

      this.isLoading = true;
      console.log('getting project view data');
      this.backendService.getProjectSummaryData(this.selectedProjectID, 'FLAIRR-Seq').subscribe((response: any) => {
        if (response.status === 200) {
          this.isLoading = false;
          const summary = response.reply as SummaryData;
          const rows = summary.data.map((row) => {
            const cloneMapLinks = this.parseCloneMapLinks(row['clone_map']);
            return {
              ...row,
              cloneMapLinks,
              ['clone_map']: cloneMapLinks.map((link) => link.label).join(', '),
            };
          });
          this.projectViewData = {
            ...summary,
            data: normalizeNumericTableRows(summary.cols, rows),
          };
          this.filteredSummaryRows = this.projectViewData.data;

          if (Object.keys(this.projectViewData).length > 0) {
            this.selectAnalysis();
          }
        } else {
          this.isLoading = false;
        }
      });
    }
  }

  selectAnalysis() {
    this.summaryFilters = this.createTableFilters();
    this.globalFilter = '';
    this.exportMessage = '';
    this.tableUpdateHandler();
  }

  isNumericColumn(column: ExportColumn): boolean {
    return isNumericTableColumn(column, this.projectViewData.data);
  }

  isReportsColumn(column: ExportColumn): boolean {
    return column.data === 'clone_map';
  }

  onGlobalFilterChange(event: Event, table: Table): void {
    this.globalFilter = (event.target as HTMLInputElement).value;
    table.filterGlobal(this.globalFilter, FilterMatchMode.CONTAINS);
  }

  tableUpdateHandler(event?: TableFilterEvent): void {
    if (event) {
      this.filteredSummaryRows = (event.filteredValue as SummaryRow[] | undefined) ?? this.projectViewData.data;
    }
    const rows = this.filteredSummaryRows;
    const selectedRows = new Set(rows);
    const unselectedRows = this.projectViewData.data.filter((row) => !selectedRows.has(row));
    this.searchActive = unselectedRows.length > 0;

    const chartRows = this.searchActive && this.compareSelectedRows ? [...rows, ...unselectedRows] : rows;
    const loci = [...new Set(chartRows.map((row) => row.locus))];
    loci.sort();
    const plotData = this.generatePlotData(loci, rows, unselectedRows);
    this.shannonPlotData = plotData.shannon;
    this.simpsonPlotData = plotData.simpson;
  }

  async copySummaryTable(): Promise<void> {
    await this.runExport('copy', () => this.exportService.copy(this.projectViewData.cols, this.filteredSummaryRows));
  }

  async exportSummaryExcel(): Promise<void> {
    await this.runExport('Excel', () =>
      this.exportService.exportExcel(this.projectViewData.cols, this.filteredSummaryRows, 'flairr-summary'),
    );
  }

  async exportSummaryPdf(): Promise<void> {
    await this.runExport('PDF', () =>
      this.exportService.exportPdf(this.projectViewData.cols, this.filteredSummaryRows, 'flairr-summary'),
    );
  }

  openCloneMapLink(event: MouseEvent): void {
    this.secureFileService.handleCellClick(event);
  }

  private createTableFilters(): TableFilters {
    return { global: { value: null, matchMode: FilterMatchMode.CONTAINS } };
  }

  private parseCloneMapLinks(value: unknown): SummaryLink[] {
    if (typeof value !== 'string' || !value.trim()) {
      return [];
    }

    const document = new DOMParser().parseFromString(value, 'text/html');
    return Array.from(document.querySelectorAll<HTMLElement>('[data-link]'))
      .map((element) => ({
        dataLink: element.getAttribute('data-link') || '',
        sample: element.getAttribute('data-sample'),
        label: element.textContent?.trim() || '',
      }))
      .filter((link) => link.dataLink.length > 0 && link.label.length > 0);
  }

  private async runExport(label: string, action: () => Promise<void>): Promise<void> {
    this.exportMessage = '';
    try {
      await action();
      this.exportMessage = label === 'copy' ? 'Table copied to clipboard.' : `${label} export downloaded.`;
    } catch (error) {
      this.exportMessage = `Could not complete ${label === 'copy' ? 'copy' : `${label} export`}.`;
      console.error(`Failed to complete ${label} for FLAIRR summary data.`, error);
    }
  }

  generatePlotData(loci: string[], rows: SummaryRow[], unselectedRows: SummaryRow[]) {
    const plotData: any = { shannon: { labels: loci, datasets: [] }, simpson: { labels: loci, datasets: [] } };

    const selectedData = this.extractDataByLocus(loci, rows);
    const firstlabel = this.searchActive ? 'Selected' : 'All';
    plotData.shannon.datasets.push({ label: firstlabel, data: selectedData.shannon });
    plotData.simpson.datasets.push({ label: firstlabel, data: selectedData.simpson });

    if (this.searchActive && this.compareSelectedRows) {
      const unselectedData = this.extractDataByLocus(loci, unselectedRows);
      plotData.shannon.datasets.push({ label: 'Unselected', data: unselectedData.shannon });
      plotData.simpson.datasets.push({ label: 'Unselected', data: unselectedData.simpson });
    }

    return plotData;
  }

  extractDataByLocus(loci: string[], rows: SummaryRow[]) {
    const data: any = { shannon: [], simpson: [] };

    loci.forEach((locus: string) => {
      const shannonVals: string[] = [];
      const simpsonVals: string[] = [];
      rows.forEach((row: SummaryRow) => {
        if (row.locus === locus) {
          shannonVals.push(row.shannon);
          simpsonVals.push(row.simpson);
        }
      });
      data.shannon.push(shannonVals);
      data.simpson.push(simpsonVals);
    });

    return data;
  }

  onCompareSelectedRows(event: any) {
    this.compareSelectedRows = event.target.checked;
    console.log('compare selected rows: ' + this.compareSelectedRows);
    this.tableUpdateHandler();
  }
}
